import React, { useCallback, useEffect, useState } from "react";
import {
    Button, Card, DatePicker, Descriptions, Modal,
    Popconfirm, Select, Space, Table, Tag, Tabs, Tooltip, Typography, message,
} from "antd";
import { CheckOutlined, RollbackOutlined, StopOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { confirmExpenseSort, revertExpenseSort } from "../../api/transactionScheduleApi";
import { formatDateTime, formatAmount, addDateColumnTools, addTextColumnTools } from "./transactionScheduleUi";
import TableMetaBar from "../common/TableMetaBar";
import {
    getInstitutions,
    getTransactionScheduleBankOptionsByInstitution,
    ignoreExpense, restoreIgnoredExpense,
} from "../../api/transactionScheduleApi";

const { Text } = Typography;

const EXPENSE_TYPE_OPTIONS = [
    { label: "Бусад шимтгэл (Other fees)", value: "EXPENSE_OTHER_FEE" },
    { label: "Буцаалтын шимтгэл (Recall fees)", value: "EXPENSE_RECALL_FEE" },
    { label: "0.15% SWIFT шимтгэл", value: "EXPENSE_SWIFT_015" },
    { label: "0.2% Комисс", value: "EXPENSE_COMMISSION_02" },
];

const EXPENSE_TYPE_LABELS = Object.fromEntries(
    EXPENSE_TYPE_OPTIONS.map((o) => [o.value, o.label])
);

function ExpenseSortActionCell({ record, onConfirmed }) {
    const [modalOpen, setModalOpen] = useState(false);
    const [expenseType, setExpenseType] = useState(undefined);
    const [sortedAt, setSortedAt] = useState(
        record?.transactionDate ? dayjs(record.transactionDate) : dayjs()
    );
    const [confirming, setConfirming] = useState(false);

    // Institution → bank account cascade
    const [institutions, setInstitutions] = useState([]);
    const [loadingInstitutions, setLoadingInstitutions] = useState(false);
    const [selectedInstitutionId, setSelectedInstitutionId] = useState(undefined);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
    const [targetBankAccountId, setTargetBankAccountId] = useState(undefined);

    // Load institutions when modal opens
    useEffect(() => {
        if (!modalOpen) return;
        setLoadingInstitutions(true);
        getInstitutions()
            .then((data) => setInstitutions(data || []))
            .catch(() => message.error("Байгууллагын жагсаалт ачаалж чадсангүй"))
            .finally(() => setLoadingInstitutions(false));
    }, [modalOpen]);

    // Load bank accounts when institution is selected
    useEffect(() => {
        if (!selectedInstitutionId) {
            setBankAccounts([]);
            setTargetBankAccountId(undefined);
            return;
        }
        setLoadingBankAccounts(true);
        setTargetBankAccountId(undefined);
        // reuse existing endpoint — returns active accounts with currency
        getTransactionScheduleBankOptionsByInstitution(selectedInstitutionId, record.currency)
            .then((resp) => {
                const accounts = Array.isArray(resp) ? resp : (resp?.bankAccounts || []);
                setBankAccounts(accounts);
            })
            .catch(() => message.error("Дансны жагсаалт ачаалж чадсангүй"))
            .finally(() => setLoadingBankAccounts(false));
    }, [selectedInstitutionId, record.currency]);

    const handleConfirm = async () => {
        if (!expenseType) { message.warning("Зардлын төрөл сонгоно уу"); return; }
        if (!targetBankAccountId) { message.warning("Зорилтот данс сонгоно уу"); return; }
        if (!sortedAt) { message.warning("Огноо сонгоно уу"); return; }

        setConfirming(true);
        try {
            await confirmExpenseSort(record.statementId, {
                expenseType,
                targetBankAccountId,
                sortedAt: sortedAt.format("YYYY-MM-DDTHH:mm:ss"),
            });
            message.success("Зардал амжилттай ангилагдлаа");
            setModalOpen(false);
            // reset state
            setExpenseType(undefined);
            setSelectedInstitutionId(undefined);
            setTargetBankAccountId(undefined);
            await onConfirmed?.();
        } catch (error) {
            message.error(error.message || "Ангилах үед алдаа гарлаа");
        } finally {
            setConfirming(false);
        }
    };

    const handleClose = () => {
        setModalOpen(false);
        setExpenseType(undefined);
        setSelectedInstitutionId(undefined);
        setTargetBankAccountId(undefined);
    };

    return (
        <>
            <Tooltip title="Ангилах">
                <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={() => setModalOpen(true)}
                />
            </Tooltip>

            <Modal
                title={`Зардал ангилах #${record.statementId}`}
                open={modalOpen}
                onCancel={handleClose}
                footer={null}
                width={600}
                destroyOnClose
            >
                <div className="space-y-4">
                    {/* Expense info */}
                    <Card size="small">
                        <div><strong>Огноо:</strong> {formatDateTime(record.transactionDate)}</div>
                        <div><strong>Байгууллага:</strong> {record.institutionName || "-"}</div>
                        <div><strong>Данс:</strong> {record.accountNumber || "-"}</div>
                        <div><strong>Зардал:</strong> {formatAmount(record.expense)} {record.currency || ""}</div>
                        <div><strong>Харьцагч:</strong> {record.counterpartyName || "-"}</div>
                        <div><strong>Утга:</strong> {record.description || "-"}</div>
                    </Card>

                    {/* Expense type */}
                    <div>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>Зардлын төрөл</div>
                        <Select
                            style={{ width: "100%" }}
                            placeholder="Зардлын төрөл сонгох"
                            value={expenseType}
                            onChange={setExpenseType}
                            options={EXPENSE_TYPE_OPTIONS}
                        />
                    </div>

                    {/* Institution select */}
                    <div>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>Байгууллага</div>
                        <Select
                            style={{ width: "100%" }}
                            placeholder="Байгууллага сонгох"
                            value={selectedInstitutionId}
                            onChange={(val) => setSelectedInstitutionId(val)}
                            loading={loadingInstitutions}
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                            options={(institutions || []).map((inst) => ({
                                label: inst.name,
                                value: inst.id,
                            }))}
                        />
                    </div>

                    {/* Bank account select — only shown after institution picked */}
                    <div>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>
                            Зорилтот данс
                            {record.currency ? (
                                <Text type="secondary" style={{ fontWeight: 400, marginLeft: 6 }}>
                                    ({record.currency} дансууд)
                                </Text>
                            ) : null}
                        </div>
                        <Select
                            style={{ width: "100%" }}
                            placeholder={
                                selectedInstitutionId
                                    ? "Данс сонгох"
                                    : "Эхлээд байгууллага сонгоно уу"
                            }
                            value={targetBankAccountId}
                            onChange={setTargetBankAccountId}
                            loading={loadingBankAccounts}
                            disabled={!selectedInstitutionId}
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                            options={(bankAccounts || [])
                                .filter((a) => a.id && a.is_active !== false)
                                .map((a) => ({
                                    label: `${a.bank || ""} — ${a.currency || ""} ${a.accountNumber}`.trim(),
                                    value: a.id,
                                }))}
                        />
                        {bankAccounts.length === 0 && selectedInstitutionId && !loadingBankAccounts && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Энэ байгууллагад идэвхтэй данс олдсонгүй
                            </Text>
                        )}
                    </div>

                    {/* Date */}
                    <div>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>Бичлэгийн огноо</div>
                        <DatePicker
                            showTime
                            value={sortedAt}
                            onChange={setSortedAt}
                            style={{ width: "100%" }}
                            format="YYYY-MM-DD HH:mm:ss"
                            allowClear={false}
                        />
                    </div>

                    <Popconfirm
                        title="Зардлыг ангилах уу?"
                        description="Энэ үйлдэл зардлыг сонгосон данс руу бичнэ."
                        okText="Батлах"
                        cancelText="Болих"
                        onConfirm={handleConfirm}
                        disabled={!expenseType || !targetBankAccountId}
                    >
                        <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            loading={confirming}
                            disabled={!expenseType || !targetBankAccountId}
                            block
                        >
                            Ангилах
                        </Button>
                    </Popconfirm>
                </div>
            </Modal>
        </>
    );
}

function ExpenseIgnoreCell({ record, onRefresh, mode = "unsorted" }) {
    const [ignoring, setIgnoring] = useState(false);
    const [restoring, setRestoring] = useState(false);

    const handleIgnore = async () => {
        setIgnoring(true);
        try {
            await ignoreExpense(record.statementId);
            message.success("Зардал үл хамаарах гэж тэмдэглэлээ");
            await onRefresh?.(true);
        } catch (error) {
            message.error(error.message || "Тэмдэглэж чадсангүй");
        } finally {
            setIgnoring(false);
        }
    };

    const handleRestore = async () => {
        setRestoring(true);
        try {
            await restoreIgnoredExpense(record.statementId);
            message.success("Зардал дахин нээлттэй болов");
            await onRefresh?.(true);
        } catch (error) {
            message.error(error.message || "Сэргээж чадсангүй");
        } finally {
            setRestoring(false);
        }
    };

    if (mode === "ignored") {
        return (
            <Tooltip title="Сэргээх">
                <Popconfirm
                    title="Зардлыг дахин нээлттэй болгох уу?"
                    okText="Сэргээх"
                    cancelText="Болих"
                    onConfirm={handleRestore}
                >
                    <Button loading={restoring} icon={<ReloadOutlined />} size="small" />
                </Popconfirm>
            </Tooltip>
        );
    }

    return (
        <Tooltip title="Үл хамаарах">
            <Popconfirm
                title="Зардал үл хамаарах гэж тэмдэглэх үү?"
                description="Нээлттэй зардлуудаас гаргаж, үл хамаарах хэсэг рүү шилжүүлнэ."
                okText="Тийм"
                cancelText="Болих"
                onConfirm={handleIgnore}
            >
                <Button danger loading={ignoring} icon={<StopOutlined />} size="small" />
            </Popconfirm>
        </Tooltip>
    );
}

function ExpenseSortingTab({
    unsortedExpenses,
    loadingUnsorted,
    sortedHistory,
    loadingHistory,
    ignoredExpenses, loadingIgnored,
    onConfirmed,
}) {
    const [unsortedMeta, setUnsortedMeta] = useState({ filteredCount: null, filters: {}, sorter: {} });
    const [historyMeta, setHistoryMeta] = useState({ filteredCount: null, filters: {}, sorter: {} });

    const handleRevert = useCallback(async (statementId) => {
        try {
            await revertExpenseSort(statementId);
            message.success("Ангилалт буцаагдлаа");
            await onConfirmed?.(true);
        } catch (error) {
            message.error(error.message || "Буцаах үед алдаа гарлаа");
        }
    }, [onConfirmed]);

    const unsortedColumns = [
        { title: "Огноо", dataIndex: "transactionDate", key: "transactionDate", width: 170, render: formatDateTime },
        { title: "Байгууллага", dataIndex: "institutionName", key: "institutionName", width: 180 },
        { title: "Данс", dataIndex: "accountNumber", key: "accountNumber", width: 160 },
        {
            title: "Зардал", key: "expense", width: 150,
            render: (_, r) => `${formatAmount(r.expense)} ${r.currency || ""}`.trim(),
        },
        { title: "Харьцагч", dataIndex: "counterpartyName", key: "counterpartyName", width: 200 },
        {
            title: "Утга", dataIndex: "description", key: "description", width: 220,
            render: (v) => <Tooltip title={v}><div style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v || "-"}</div></Tooltip>
        },
        // In unsortedColumns, replace the action column:
        {
            title: "Үйлдэл", key: "action", width: 120, fixed: "right",
            render: (_, record) => (
                <Space>
                    <ExpenseSortActionCell
                        record={record}
                        onConfirmed={() => onConfirmed?.(true)}
                    />
                    <ExpenseIgnoreCell
                        record={record}
                        onRefresh={onConfirmed}
                        mode="unsorted"
                    />
                </Space>
            ),
        }
    ].map((col) => {
        if (col.key === "transactionDate") return addDateColumnTools(col, "transactionDate", "descend");
        if (col.key === "institutionName") return addTextColumnTools(col, unsortedExpenses, "institutionName");
        if (col.key === "expense") return { ...col, sorter: (a, b) => Number(a.expense || 0) - Number(b.expense || 0) };
        return col;
    });

    const historyColumns = [
        { title: "Ангилсан огноо", dataIndex: "sortedAt", key: "sortedAt", width: 170, render: formatDateTime },
        { title: "Ангилсан хэрэглэгч", dataIndex: "sortedBy", key: "sortedBy", width: 140 },
        { title: "Гүйлгээний огноо", dataIndex: "transactionDate", key: "transactionDate", width: 170, render: formatDateTime },
        { title: "Байгууллага", dataIndex: "institutionName", key: "institutionName", width: 180 },
        {
            title: "Зардлын төрөл", dataIndex: "expenseType", key: "expenseType", width: 200,
            render: (v) => <Tag color="orange">{EXPENSE_TYPE_LABELS[v] || v}</Tag>,
        },
        {
            title: "Зардал", key: "expense", width: 150,
            render: (_, r) => `${formatAmount(r.expense)} ${r.currency || ""}`.trim(),
        },
        { title: "Зорилтот данс", dataIndex: "targetAccountNumber", key: "targetAccountNumber", width: 160 },
        {
            title: "Буцаах", key: "revert", width: 90, fixed: "right",
            render: (_, record) => (
                <Popconfirm
                    title="Ангилалтыг буцаах уу?"
                    description="Энэ үйлдэл report statement-г устгана."
                    okText="Буцаах"
                    cancelText="Болих"
                    onConfirm={() => handleRevert(record.statementId)}
                >
                    <Tooltip title="Буцаах">
                        <Button danger icon={<RollbackOutlined />} size="small" />
                    </Tooltip>
                </Popconfirm>
            ),
        },
    ].map((col) => {
        if (col.key === "sortedAt") return addDateColumnTools(col, "sortedAt", "descend");
        if (col.key === "institutionName") return addTextColumnTools(col, sortedHistory, "institutionName");
        return col;
    });

    const ignoredColumns = [
        { title: "Тэмдэглэсэн огноо", dataIndex: "ignoredAt", key: "ignoredAt", width: 170, render: formatDateTime },
        { title: "Тэмдэглэсэн", dataIndex: "ignoredBy", key: "ignoredBy", width: 140 },
        { title: "Гүйлгээний огноо", dataIndex: "transactionDate", key: "transactionDate", width: 170, render: formatDateTime },
        { title: "Байгууллага", dataIndex: "institutionName", key: "institutionName", width: 180 },
        {
            title: "Зардал", key: "expense", width: 150,
            render: (_, r) => `${formatAmount(r.expense)} ${r.currency || ""}`.trim()
        },
        { title: "Харьцагч", dataIndex: "counterpartyName", key: "counterpartyName", width: 200 },
        {
            title: "Үйлдэл", key: "action", width: 90, fixed: "right",
            render: (_, record) => (
                <ExpenseIgnoreCell
                    record={record}
                    onRefresh={onConfirmed}
                    mode="ignored"
                />
            ),
        },
    ];

    return (
        <Card title="Зардал ангилах">
            <Tabs
                defaultActiveKey="unsorted"
                items={[
                    {
                        key: "unsorted",
                        label: `Ангилагдаагүй зардлууд (${unsortedExpenses.length})`,
                        children: (
                            <>
                                <div className="mb-3 flex gap-2 flex-wrap">
                                    <Tag color="red">Нийт: {unsortedExpenses.length}</Tag>
                                </div>
                                <TableMetaBar
                                    totalCount={unsortedExpenses.length}
                                    visibleCount={unsortedMeta.filteredCount}
                                    filters={unsortedMeta.filters}
                                    sorter={unsortedMeta.sorter}
                                />
                                <Table
                                    rowKey="statementId"
                                    columns={unsortedColumns}
                                    dataSource={unsortedExpenses}
                                    loading={loadingUnsorted}
                                    pagination={{ pageSize: 10 }}
                                    scroll={{ x: "max-content" }}
                                    bordered
                                    size="small"
                                    sortDirections={["ascend", "descend"]}
                                    onChange={(_, filters, sorter, extra) =>
                                        setUnsortedMeta({ filteredCount: extra?.currentDataSource?.length ?? null, filters, sorter })
                                    }
                                    expandable={{
                                        expandedRowRender: (record) => (
                                            <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
                                                <Descriptions.Item label="Утга" span={2}>
                                                    <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{record.description || "-"}</div>
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Statement ID">{record.statementId}</Descriptions.Item>
                                                <Descriptions.Item label="Валют">{record.currency || "-"}</Descriptions.Item>
                                            </Descriptions>
                                        ),
                                        rowExpandable: () => true,
                                    }}
                                />
                            </>
                        ),
                    },
                    {
                        key: "history",
                        label: `Ангилсан түүх (${sortedHistory.length})`,
                        children: (
                            <>
                                <TableMetaBar
                                    totalCount={sortedHistory.length}
                                    visibleCount={historyMeta.filteredCount}
                                    filters={historyMeta.filters}
                                    sorter={historyMeta.sorter}
                                />
                                <Table
                                    rowKey="key"
                                    columns={historyColumns}
                                    dataSource={sortedHistory}
                                    loading={loadingHistory}
                                    pagination={{ pageSize: 10 }}
                                    scroll={{ x: "max-content" }}
                                    bordered
                                    size="small"
                                    sortDirections={["ascend", "descend"]}
                                    onChange={(_, filters, sorter, extra) =>
                                        setHistoryMeta({ filteredCount: extra?.currentDataSource?.length ?? null, filters, sorter })
                                    }
                                />
                            </>
                        ),
                    },

                    {
                        key: "ignored",
                        label: `Үл хамаарах (${ignoredExpenses.length})`,
                        children: (
                            <Table
                                rowKey="statementId"
                                columns={ignoredColumns}
                                dataSource={ignoredExpenses}
                                loading={loadingIgnored}
                                pagination={{ pageSize: 10 }}
                                scroll={{ x: "max-content" }}
                                bordered
                                size="small"
                            />
                        ),
                    },
                ]}
            />
        </Card>
    );
}

export default ExpenseSortingTab;